/**
 * Unified Google Analytics Module
 *
 * This module provides a consistent API for Google Analytics tracking
 * across both the main app and country-profiles sections.
 *
 * Uses react-ga4 internally, which is initialized in main.tsx
 *
 * NOTE: All tracking is gated by import.meta.env.PROD to prevent
 * development/staging traffic from polluting production analytics.
 */

import { useEffect } from "react";
import { useLocation } from "react-router";
import ReactGAModule from "react-ga4";
const ReactGA = (ReactGAModule as any).default ?? ReactGAModule;
import {
  engagementGAEventName,
  performanceMetricsGAEventName,
} from "../../buildConstants";

// ============================================
// Environment Check
// ============================================

/**
 * Check if we should track analytics.
 * Uses Vite's native import.meta.env.PROD which is guaranteed to be
 * true in production builds and false in development.
 */
const shouldTrack = (): boolean => {
  return import.meta.env.PROD;
};

// ============================================
// Page View Tracking
// ============================================

/**
 * Track a page view
 * @param page - The page path (e.g., "/countries/usa")
 */
export const trackPageView = (page: string) => {
  if (!shouldTrack()) return;

  ReactGA.send({
    hitType: "pageview",
    page,
    page_location: window.location.origin + page,
  });
};

/**
 * Hook for automatic page view tracking on route changes.
 * Use this in layout components to track all route changes.
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const page = location.pathname + location.search;
    trackPageView(page);
  }, [location]);
};

// ============================================
// Custom Event Tracking
// ============================================

export interface TrackEventOptions {
  category?: string;
  action: string;
  label?: string;
  element?: string;
  location?: string;
  value?: number;
  nonInteraction?: boolean;
}

/**
 * Track a custom event
 */
export const trackEvent = ({
  category = engagementGAEventName,
  action,
  label,
  element,
  location,
  value,
  nonInteraction,
}: TrackEventOptions) => {
  if (!shouldTrack()) return;

  ReactGA.event(action, {
    category,
    event_category: category,
    event_label: label,
    element,
    location,
    value,
    non_interaction: nonInteraction,
  });
};

// ============================================
// Specialized Event Functions
// ============================================

/**
 * Track user engagement events (clicks, interactions, etc.)
 * This is the primary function for tracking user interactions.
 */
export const trackEngagementEvent = (
  action: string,
  label: string,
  element?: string,
  location?: string,
) => {
  trackEvent({
    category: engagementGAEventName,
    action,
    label,
    element,
    location,
  });
};

/**
 * Track performance metrics (load times, etc.)
 */
export const trackPerformanceEvent = (
  action: string,
  label: string,
  value?: number,
  nonInteraction: boolean = true,
) => {
  trackEvent({
    category: performanceMetricsGAEventName,
    action,
    label,
    value,
    nonInteraction,
  });
};

/**
 * Track export actions
 */
export const trackExportEvent = (graphType: string, exportType: string) => {
  trackEvent({
    category: engagementGAEventName,
    action: "Export",
    label: `${graphType} - ${exportType}`,
  });
};

/**
 * Track hero element timing for performance monitoring
 */
export const trackHeroElementTiming = (
  heroElementName: string,
  timeMs: number,
) => {
  // Main hero element event
  trackEvent({
    category: performanceMetricsGAEventName,
    action: "Hero Element",
    label: heroElementName,
    value: timeMs,
    nonInteraction: true,
  });

  // Additional timing event for load category
  trackEvent({
    category: "load",
    action: "Hero Element Timing",
    label: heroElementName,
    value: timeMs,
  });
};

// ============================================
// Legacy Compatibility Exports
// ============================================

/**
 * Legacy enum for backward compatibility with existing code
 */
export enum GoogleAnalyticsCaptureType {
  PageView = "pageview",
  CustomEvent = "customevent",
}

/**
 * Legacy function signature for backward compatibility with main app code.
 * @deprecated Use trackPageView() or trackEvent() directly instead
 */
export const googleAnalyticsEvent = (
  captureType: GoogleAnalyticsCaptureType,
  category: string,
  action: string,
  label: string,
  location: string,
) => {
  if (captureType === GoogleAnalyticsCaptureType.PageView) {
    trackPageView(label);
  } else if (captureType === GoogleAnalyticsCaptureType.CustomEvent) {
    trackEvent({
      category,
      action,
      label,
      location,
    });
  }
};

/**
 * Legacy function for custom events from main app code.
 * @deprecated Use trackEvent() or trackEngagementEvent() directly instead
 */
export const googleAnalyticsCustomEvent = (
  action: string,
  category: string,
  label: string,
  element: string,
) => {
  trackEvent({
    category,
    action,
    label,
    element,
  });
};

// Default export for backward compatibility
export default googleAnalyticsEvent;

